import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ShoppingCart, Plus, Minus } from 'lucide-react';
import { useCart } from '../hooks/useCart';
import { formatCurrency } from '../../../utils/format';

interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  salePrice: number;
  image: string;
  unit: string;
  stock: number;
}

interface ProductCardProps {
  product: Product;
}

export default function ProductCard({ product }: ProductCardProps) {
  const { storeSlug } = useParams();
  const { items, addItem, updateQuantity } = useCart();
  const cartItem = items.find(item => item.id === product.id);

  const handleAddToCart = () => {
    if (!cartItem) {
      addItem({
        id: product.id,
        name: product.name,
        price: product.salePrice > 0 ? product.salePrice : product.price,
        quantity: 1,
        image: product.image,
        unit: product.unit
      });
    } else {
      updateQuantity(product.id, cartItem.quantity + 1);
    }
  };

  const handleDecreaseQuantity = () => {
    if (cartItem && cartItem.quantity > 1) {
      updateQuantity(product.id, cartItem.quantity - 1);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
      <Link to={`/shop/${storeSlug}/product/${product.id}`} className="block">
        <div className="relative aspect-square overflow-hidden rounded-t-lg">
          <img
            src={product.image}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
          />
          {product.salePrice > 0 && (
            <span className="absolute top-2 right-2 bg-[#C5D86D] text-[#0F172A] text-xs font-bold px-2 py-1 rounded">
              PROMOÇÃO
            </span>
          )}
        </div>
      </Link>
      
      <div className="p-4">
        <Link to={`/shop/${storeSlug}/product/${product.id}`} className="block">
          <h3 className="text-sm font-medium text-[#0F172A] truncate mb-1">
            {product.name}
          </h3>
          <div className="mb-4">
            {product.salePrice > 0 ? (
              <div className="space-y-1">
                <span className="text-lg font-bold text-[#C5D86D]">
                  {formatCurrency(product.salePrice)}
                </span>
                <span className="ml-2 text-sm text-gray-500 line-through">
                  {formatCurrency(product.price)}
                </span>
              </div>
            ) : (
              <span className="text-lg font-bold text-[#0F172A]">
                {formatCurrency(product.price)}
              </span>
            )}
          </div>
        </Link>

        <div className="mt-2">
          {!cartItem ? (
            <button
              onClick={handleAddToCart}
              disabled={product.stock === 0}
              className={`w-full flex items-center justify-center space-x-2 px-4 py-2 text-sm font-medium rounded-md ${
                product.stock === 0
                  ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                  : 'bg-[#0F172A] text-white hover:bg-[#1E293B]'
              }`}
            >
              <ShoppingCart className="h-4 w-4" />
              <span>{product.stock === 0 ? 'Sem stock' : 'Adicionar'}</span>
            </button>
          ) : (
            <div className="flex items-center justify-between">
              <button
                onClick={handleDecreaseQuantity}
                className="p-2 text-[#0F172A] hover:bg-[#0F172A] hover:text-white rounded-md transition-colors"
              >
                <Minus className="h-4 w-4" />
              </button>
              <span className="font-medium text-[#0F172A]">{cartItem.quantity}</span>
              <button
                onClick={handleAddToCart}
                className="p-2 text-[#0F172A] hover:bg-[#0F172A] hover:text-white rounded-md transition-colors"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
